import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getClients, createClient, deleteClient, getReferrals } from '../services/api';
import {
  Plus,
  Search,
  Eye,
  Trash2,
  Building2,
  User,
  Phone,
  Mail,
  X,
  Loader2,
  Edit,
  MapPin,
  Upload,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const clientTypes = {
  1: { label: 'Owner', color: 'text-slate-700' },
  2: { label: 'Real State/Strata', color: 'text-slate-700' },
  3: { label: 'Tenant', color: 'text-slate-700' },
};

function ClientModal({ isOpen, onClose, onSubmit, loading }) {
  const defaultFormData = {
    contact_name: '',
    website: '',
    tax_number: '',
    client_type: '',
    referral_id: '',
    contact_first_name: '',
    contact_last_name: '',
    contact_email: '',
    contact_phone: '',
    contact_phone2: '',
    contact_phone3: '',
    sa_address_line1: '',
    sa_address_line2: '',
    sa_address_line3: '',
    sa_address_line4: '',
    sa_city: '',
    sa_region: 'WA',
    sa_country: 'Australia',
    sa_postal_code: '',
    is_same_address: false,
    po_address_line1: '',
    po_address_line2: '',
    po_address_line3: '',
    po_address_line4: '',
    po_city: '',
    po_region: 'WA',
    po_country: 'Australia',
    po_postal_code: '',
  };
  
  const [formData, setFormData] = useState(defaultFormData);

  const { data: referrals } = useQuery({
    queryKey: ['referrals'],
    queryFn: () => getReferrals().then(res => res.data),
    enabled: isOpen
  });

  React.useEffect(() => {
    if (isOpen) setFormData(defaultFormData);
  }, [isOpen]);

  const handleSameAddress = (checked) => {
    if (checked) {
      setFormData({
        ...formData,
        is_same_address: true,
        po_address_line1: formData.sa_address_line1,
        po_address_line2: formData.sa_address_line2,
        po_address_line3: formData.sa_address_line3,
        po_address_line4: formData.sa_address_line4,
        po_city: formData.sa_city,
        po_region: formData.sa_region,
        po_country: formData.sa_country,
        po_postal_code: formData.sa_postal_code,
      });
    } else {
      setFormData({ ...formData, is_same_address: false });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.contact_name) {
      toast.error('Please enter client name');
      return;
    }
    // Strip empty strings for fields that backend expects as int/optional so
    // Pydantic defaults kick in (client_type defaults to 1 = Owner).
    const payload = { ...formData };
    if (payload.client_type === '' || payload.client_type === null || payload.client_type === undefined) {
      delete payload.client_type;
    }
    if (!payload.referral_id) {
      delete payload.referral_id;
    }
    onSubmit(payload);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="p-5 border-b flex items-center justify-center relative bg-slate-50">
          <div className="flex items-center gap-2">
            <User size={20} className="text-slate-600" />
            <h2 className="text-lg font-semibold text-slate-700">Create New Client</h2>
          </div>
          <button onClick={onClose} className="absolute right-4 text-slate-400 hover:text-slate-600">
            <X size={24} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6">
          {/* Client Details Section */}
          <div className="mb-6">
            <h3 className="flex items-center gap-2 text-primary font-semibold mb-4">
              <User size={18} />
              Client Details
            </h3>
            <div className="space-y-4">
              <div className="flex items-center">
                <label className="w-32 text-sm text-slate-500 text-right pr-4 flex-shrink-0">Name</label>
                <input type="text" value={formData.contact_name} onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })} className="flex-1 px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none" data-testid="client-name-input" />
              </div>
              <div className="flex items-center">
                <label className="w-32 text-sm text-slate-500 text-right pr-4 flex-shrink-0">Website</label>
                <input type="text" value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} className="flex-1 px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none" />
              </div>
              <div className="flex items-center">
                <label className="w-32 text-sm text-slate-500 text-right pr-4 flex-shrink-0">Tax Number</label>
                <input type="text" value={formData.tax_number} onChange={(e) => setFormData({ ...formData, tax_number: e.target.value })} className="flex-1 px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none" />
              </div>
              <div className="flex items-center">
                <label className="w-32 text-sm text-slate-500 text-right pr-4 flex-shrink-0">Type</label>
                <select value={formData.client_type} onChange={(e) => setFormData({ ...formData, client_type: e.target.value ? parseInt(e.target.value) : '' })} className="w-64 px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none bg-white" data-testid="client-type-select">
                  <option value="">Select Client Type</option>
                  <option value="1">Owner</option>
                  <option value="2">Real State/Strata</option>
                  <option value="3">Tenant</option>
                </select>
              </div>
              <div className="flex items-center">
                <label className="w-32 text-sm text-slate-500 text-right pr-4 flex-shrink-0">Referral</label>
                <select value={formData.referral_id} onChange={(e) => setFormData({ ...formData, referral_id: e.target.value })} className="w-64 px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none bg-white">
                  <option value="">Select Referral</option>
                  {referrals?.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Contact Person Section */}
          <div className="mb-6">
            <h3 className="flex items-center gap-2 text-primary font-semibold mb-4"><User size={18} />Contact Person</h3>
            <div className="space-y-4">
              <div className="flex items-center">
                <label className="w-32 text-sm text-slate-500 text-right pr-4 flex-shrink-0">Name</label>
                <div className="flex-1 flex gap-3">
                  <input type="text" value={formData.contact_first_name} onChange={(e) => setFormData({ ...formData, contact_first_name: e.target.value })} placeholder="First name" className="flex-1 px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none" />
                  <input type="text" value={formData.contact_last_name} onChange={(e) => setFormData({ ...formData, contact_last_name: e.target.value })} placeholder="Last name" className="flex-1 px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none" />
                </div>
              </div>
              <div className="flex items-center">
                <label className="w-32 text-sm text-slate-500 text-right pr-4 flex-shrink-0">Email Address</label>
                <input type="email" value={formData.contact_email} onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })} className="flex-1 px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none" />
              </div>
              <div className="flex items-center">
                <label className="w-32 text-sm text-slate-500 text-right pr-4 flex-shrink-0">Phone Number 1</label>
                <input type="text" value={formData.contact_phone} onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })} className="flex-1 px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none" />
              </div>
              <div className="flex items-center">
                <label className="w-32 text-sm text-slate-500 text-right pr-4 flex-shrink-0">Phone Number 2</label>
                <input type="text" value={formData.contact_phone2} onChange={(e) => setFormData({ ...formData, contact_phone2: e.target.value })} className="flex-1 px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none" />
              </div>
              <div className="flex items-center">
                <label className="w-32 text-sm text-slate-500 text-right pr-4 flex-shrink-0">Phone Number 3</label>
                <input type="text" value={formData.contact_phone3} onChange={(e) => setFormData({ ...formData, contact_phone3: e.target.value })} className="flex-1 px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none" />
              </div>
            </div>
          </div>

          {/* Street Address Section */}
          <div className="mb-6">
            <h3 className="flex items-center gap-2 text-primary font-semibold mb-4"><MapPin size={18} />Street Address</h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <label className="w-32 text-sm text-slate-500 text-right pr-4 flex-shrink-0 pt-2">Complete Address</label>
                <div className="flex-1 space-y-2">
                  <input type="text" value={formData.sa_address_line1} onChange={(e) => setFormData({ ...formData, sa_address_line1: e.target.value })} placeholder="Address Line 1" className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none" />
                  <input type="text" value={formData.sa_address_line2} onChange={(e) => setFormData({ ...formData, sa_address_line2: e.target.value })} placeholder="Address Line 2" className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none" />
                  <input type="text" value={formData.sa_address_line3} onChange={(e) => setFormData({ ...formData, sa_address_line3: e.target.value })} placeholder="Address Line 3" className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none" />
                  <input type="text" value={formData.sa_address_line4} onChange={(e) => setFormData({ ...formData, sa_address_line4: e.target.value })} placeholder="Address Line 4" className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none" />
                </div>
              </div>
              <div className="flex items-center">
                <label className="w-32 text-sm text-slate-500 text-right pr-4 flex-shrink-0">City</label>
                <input type="text" value={formData.sa_city} onChange={(e) => setFormData({ ...formData, sa_city: e.target.value })} className="flex-1 px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none" />
              </div>
              <div className="flex items-center">
                <label className="w-32 text-sm text-slate-500 text-right pr-4 flex-shrink-0">Region</label>
                <input type="text" value={formData.sa_region} onChange={(e) => setFormData({ ...formData, sa_region: e.target.value })} className="flex-1 px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none" />
              </div>
              <div className="flex items-center">
                <label className="w-32 text-sm text-slate-500 text-right pr-4 flex-shrink-0">Country</label>
                <input type="text" value={formData.sa_country} onChange={(e) => setFormData({ ...formData, sa_country: e.target.value })} className="flex-1 px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none" />
              </div>
              <div className="flex items-center">
                <label className="w-32 text-sm text-slate-500 text-right pr-4 flex-shrink-0">Postal Code</label>
                <input type="text" value={formData.sa_postal_code} onChange={(e) => setFormData({ ...formData, sa_postal_code: e.target.value })} className="w-48 px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none" />
              </div>
            </div>
          </div>

          {/* Postal Address Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="flex items-center gap-2 text-primary font-semibold"><MapPin size={18} />Postal Address</h3>
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <input type="checkbox" checked={formData.is_same_address} onChange={(e) => handleSameAddress(e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary" />
                Same as Street address
              </label>
            </div>
            <div className="space-y-4">
              <div className="flex items-start">
                <label className="w-32 text-sm text-slate-500 text-right pr-4 flex-shrink-0 pt-2">Complete Address</label>
                <div className="flex-1 space-y-2">
                  <input type="text" value={formData.po_address_line1} onChange={(e) => setFormData({ ...formData, po_address_line1: e.target.value })} placeholder="Address Line 1" className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none" disabled={formData.is_same_address} />
                  <input type="text" value={formData.po_address_line2} onChange={(e) => setFormData({ ...formData, po_address_line2: e.target.value })} placeholder="Address Line 2" className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none" disabled={formData.is_same_address} />
                  <input type="text" value={formData.po_address_line3} onChange={(e) => setFormData({ ...formData, po_address_line3: e.target.value })} placeholder="Address Line 3" className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none" disabled={formData.is_same_address} />
                  <input type="text" value={formData.po_address_line4} onChange={(e) => setFormData({ ...formData, po_address_line4: e.target.value })} placeholder="Address Line 4" className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none" disabled={formData.is_same_address} />
                </div>
              </div>
              <div className="flex items-center">
                <label className="w-32 text-sm text-slate-500 text-right pr-4 flex-shrink-0">City</label>
                <input type="text" value={formData.po_city} onChange={(e) => setFormData({ ...formData, po_city: e.target.value })} className="flex-1 px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none" disabled={formData.is_same_address} />
              </div>
              <div className="flex items-center">
                <label className="w-32 text-sm text-slate-500 text-right pr-4 flex-shrink-0">Region</label>
                <input type="text" value={formData.po_region} onChange={(e) => setFormData({ ...formData, po_region: e.target.value })} className="flex-1 px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none" disabled={formData.is_same_address} />
              </div>
              <div className="flex items-center">
                <label className="w-32 text-sm text-slate-500 text-right pr-4 flex-shrink-0">Country</label>
                <input type="text" value={formData.po_country} onChange={(e) => setFormData({ ...formData, po_country: e.target.value })} className="flex-1 px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none" disabled={formData.is_same_address} />
              </div>
              <div className="flex items-center">
                <label className="w-32 text-sm text-slate-500 text-right pr-4 flex-shrink-0">Postal Code</label>
                <input type="text" value={formData.po_postal_code} onChange={(e) => setFormData({ ...formData, po_postal_code: e.target.value })} className="w-48 px-3 py-2 border border-slate-300 rounded focus:ring-2 focus:ring-primary focus:border-primary outline-none" disabled={formData.is_same_address} />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={onClose} className="px-5 py-2 border border-slate-300 rounded text-slate-600 hover:bg-slate-50">Cancel</button>
            <button type="submit" disabled={loading} className="px-5 py-2 bg-[#3498db] text-white rounded hover:bg-[#2980b9] flex items-center gap-2" data-testid="save-client-btn">
              {loading && <Loader2 size={16} className="animate-spin" />}
              Add
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Clients() {
  const [search, setSearch] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: clients, isLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: () => getClients({}).then(res => res.data),
  });

  const createMutation = useMutation({
    mutationFn: createClient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setShowModal(false);
      toast.success('Client created successfully');
    },
    onError: (error) => {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => `${d.loc?.slice(-1)[0] || 'field'}: ${d.msg}`).join('; ')
        : detail || 'Failed to create client';
      toast.error(msg);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteClient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Client removed successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to remove client');
    },
  });

  // Filter clients by search
  const filteredClients = clients?.filter(c => 
    c.contact_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.contact_email?.toLowerCase().includes(search.toLowerCase()) ||
    c.contact_phone?.includes(search)
  ) || [];

  // Pagination
  const totalPages = Math.ceil(filteredClients.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const paginatedClients = filteredClients.slice(startIndex, startIndex + entriesPerPage);

  const handleDelete = (id, name) => {
    if (window.confirm(`Are you sure you want to remove "${name}"?`)) {
      deleteMutation.mutate(id);
    }
  };

  const getContactPerson = (client) => {
    const parts = [client.contact_first_name, client.contact_last_name].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : '-';
  };

  return (
    <div className="animate-fadeIn" data-testid="clients-page">
      {/* Header */}
      <h1 className="text-2xl font-bold text-slate-800 mb-6">Clients</h1>

      {/* Main Card */}
      <div className="bg-white rounded-lg border shadow-sm">
        {/* Top Controls */}
        <div className="p-4 flex items-center justify-between border-b">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <span>Show</span>
            <select 
              value={entriesPerPage} 
              onChange={(e) => { setEntriesPerPage(Number(e.target.value)); setCurrentPage(1); }}
              className="border rounded px-2 py-1"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>entries</span>
          </div>
          
          <div className="flex items-center gap-3">
            <button className="flex items-center gap-2 px-4 py-2 bg-[#3498db] text-white rounded hover:bg-[#2980b9]">
              <Upload size={16} />
              Import Contacts
            </button>
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#18bc9c] text-white rounded hover:bg-[#15a589]"
              data-testid="add-client-btn"
            >
              <Plus size={16} />
              Create New Client
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="p-4 flex justify-end border-b">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">Search:</span>
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              className="border rounded px-3 py-1.5 w-64"
              data-testid="search-clients-input"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b">
              <tr className="text-left text-sm text-slate-600">
                <th className="px-4 py-3 font-medium">Name <span className="text-slate-400">↕</span></th>
                <th className="px-4 py-3 font-medium">Type <span className="text-slate-400">↕</span></th>
                <th className="px-4 py-3 font-medium">Contact Person <span className="text-slate-400">↕</span></th>
                <th className="px-4 py-3 font-medium">Contact Number <span className="text-slate-400">↕</span></th>
                <th className="px-4 py-3 font-medium">Email <span className="text-slate-400">↕</span></th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <Loader2 size={24} className="animate-spin mx-auto text-primary" />
                  </td>
                </tr>
              ) : paginatedClients.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                    No clients found
                  </td>
                </tr>
              ) : (
                paginatedClients.map((client) => (
                  <tr key={client.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <Link to={`/clients/${client.id}`} className="text-[#3498db] hover:underline">
                        {client.contact_name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {clientTypes[client.client_type]?.label || 'na'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {getContactPerson(client)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {client.contact_phone || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-[#3498db]">
                      {client.contact_email || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleDelete(client.id, client.contact_name)}
                          className="px-3 py-1.5 bg-[#e74c3c] text-white text-xs rounded hover:bg-[#c0392b]"
                          data-testid={`remove-client-${client.id}`}
                        >
                          Remove
                        </button>
                        <Link
                          to={`/clients/${client.id}?edit=true`}
                          className="px-3 py-1.5 bg-[#f39c12] text-white text-xs rounded hover:bg-[#d68910]"
                          data-testid={`edit-client-${client.id}`}
                        >
                          Edit
                        </Link>
                        <Link
                          to={`/clients/${client.id}`}
                          className="px-3 py-1.5 bg-[#3498db] text-white text-xs rounded hover:bg-[#2980b9]"
                          data-testid={`details-client-${client.id}`}
                        >
                          Details
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="p-4 flex items-center justify-between border-t text-sm">
          <div className="text-slate-600">
            Showing {filteredClients.length === 0 ? 0 : startIndex + 1} to {Math.min(startIndex + entriesPerPage, filteredClients.length)} of {filteredClients.length} entries
          </div>
          <div className="flex items-center gap-1">
            <button 
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 border rounded text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              Previous
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-3 py-1.5 border rounded ${currentPage === pageNum ? 'bg-[#3498db] text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                >
                  {pageNum}
                </button>
              );
            })}
            {totalPages > 5 && currentPage < totalPages - 2 && (
              <>
                <span className="px-2 text-slate-400">...</span>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  className="px-3 py-1.5 border rounded text-slate-600 hover:bg-slate-50"
                >
                  {totalPages}
                </button>
              </>
            )}
            <button 
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages || totalPages === 0}
              className="px-3 py-1.5 border rounded text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <ClientModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={(data) => createMutation.mutate(data)}
        loading={createMutation.isPending}
      />
    </div>
  );
}

export default Clients;
